import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Zap, Award, Loader2, Trophy, MapPin, Calendar, X, Play, Youtube, Download, Video, FileText } from 'lucide-react';
import { buildLogsAPI, achievementsAPI, youtubeAPI, documentsAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageContent, getLocalizedField } from '../hooks/usePageContent';
import { usePageTitle } from '../hooks/usePageTitle';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

const Home = () => {
  const [latestLogs, setLatestLogs] = useState([]);
  const [featuredAchievements, setFeaturedAchievements] = useState([]);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const { t, language } = useLanguage();
  const { content, getContent } = usePageContent('home');
  
  // Dynamic page title
  usePageTitle('nav.home', 'Home', 'Ana Sayfa');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [logsData, achievementsData, videosData, docsData] = await Promise.all([
          buildLogsAPI.getAll(),
          achievementsAPI.getFeatured(),
          youtubeAPI.getAll(),
          documentsAPI.getAll().catch(() => [])
        ]);
        setLatestLogs(logsData.slice(0, 3));
        setFeaturedAchievements(achievementsData.slice(0, 3));
        setYoutubeVideos(videosData.slice(0, 3));
        setDocuments(docsData || []);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Close popup on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setSelectedAchievement(null);
        setSelectedVideo(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Get featured color styles
  const getFeaturedStyle = (color) => {
    const styles = {
      yellow: { iconColor: '#FFD700', shadowColor: 'rgba(255, 215, 0, 0.3)', borderColor: 'rgba(255, 215, 0, 0.5)', bgGradient: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(15, 23, 42, 0.95) 100%)' },
      red: { iconColor: '#EF4444', shadowColor: 'rgba(239, 68, 68, 0.3)', borderColor: 'rgba(239, 68, 68, 0.5)', bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(15, 23, 42, 0.95) 100%)' },
      blue: { iconColor: '#00F0FF', shadowColor: 'rgba(0, 240, 255, 0.3)', borderColor: 'rgba(0, 240, 255, 0.5)', bgGradient: 'linear-gradient(135deg, rgba(0, 240, 255, 0.15) 0%, rgba(15, 23, 42, 0.95) 100%)' },
      green: { iconColor: '#22C55E', shadowColor: 'rgba(34, 197, 94, 0.3)', borderColor: 'rgba(34, 197, 94, 0.5)', bgGradient: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(15, 23, 42, 0.95) 100%)' }
    };
    return styles[color] || styles.yellow;
  };

  return (
    <div className="min-h-screen" style={{ background: '#0F172A' }} data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Gradient Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getContent('hero_image') || 'https://images.unsplash.com/photo-1550660460-9a5f483a68a4?w=1920'}')`
          }}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.7) 50%, rgba(0, 240, 255, 0.3) 100%)'
          }}></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-[7.6923%] text-center">
          <h1 className="display-huge mb-6 animate-fadeIn whitespace-pre-line" style={{
            color: '#F8FAFC',
            textShadow: '0 0 20px rgba(0, 240, 255, 0.5), 0 0 40px rgba(0, 240, 255, 0.3)'
          }}>
            {getContent('hero_title', t, 'home.hero.title', language)}
          </h1>
          <p className="body-large max-w-3xl mx-auto mb-10" style={{ color: '#00F0FF', fontWeight: 500 }}>
            {getContent('hero_subtitle', t, 'home.hero.subtitle', language)}
          </p>
          <Link 
            to="/aircraft" 
            className="btn-primary inline-flex"
            data-testid="explore-aircraft-btn"
          >
            {getContent('hero_cta', t, 'home.hero.cta', language)}
            <ArrowRight size={20} />
          </Link>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 flex justify-center pt-2" style={{
            border: '2px solid rgba(0, 240, 255, 0.6)',
            borderRadius: '20px',
            boxShadow: '0 0 15px rgba(0, 240, 255, 0.3)'
          }}>
            <div className="w-1 h-3 rounded-full" style={{
              background: '#00F0FF',
              boxShadow: '0 0 10px rgba(0, 240, 255, 0.8)'
            }}></div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-24 px-[7.6923%] relative" style={{
        background: 'rgba(30, 41, 59, 0.4)',
        backdropFilter: 'blur(8px)'
      }}>
        <div className="absolute inset-0 grid-pattern pointer-events-none"></div>
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="display-medium mb-8" style={{ color: '#F8FAFC' }}>
              {getContent('mission_title', t, 'home.mission.title', language)}
            </h2>
            <p className="body-large leading-relaxed" style={{ color: '#F8FAFC', opacity: 0.9 }}>
              {getContent('mission_description', t, 'home.mission.description', language)}
            </p>
          </div>
        </div>
      </section>

      {/* Competition Focus */}
      <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="display-medium mb-6" style={{ color: '#F8FAFC' }}>
              {getContent('features_title', t, 'home.features.title', language)}
            </h2>
            <p className="body-medium max-w-3xl mx-auto" style={{ color: '#F8FAFC', opacity: 0.85 }}>
              {getContent('features_subtitle', t, 'home.features.subtitle', language)}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center p-8 glass-card dark-transition dark-hover" data-testid="feature-navigation">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{
                background: 'rgba(0, 240, 255, 0.15)',
                boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
              }}>
                <Target size={32} style={{ color: '#00F0FF' }} />
              </div>
              <h3 className="heading-2 mb-4" style={{ color: '#F8FAFC' }}>
                {getContent('feature1_title', t, 'home.features.autonomous.title', language)}
              </h3>
              <p className="body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                {getContent('feature1_description', t, 'home.features.autonomous.description', language)}
              </p>
            </div>

            <div className="text-center p-8 glass-card dark-transition dark-hover" data-testid="feature-odlc">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{
                background: 'rgba(0, 240, 255, 0.15)',
                boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
              }}>
                <Zap size={32} style={{ color: '#00F0FF' }} />
              </div>
              <h3 className="heading-2 mb-4" style={{ color: '#F8FAFC' }}>
                {getContent('feature2_title', t, 'home.features.payload.title', language)}
              </h3>
              <p className="body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                {getContent('feature2_description', t, 'home.features.payload.description', language)}
              </p>
            </div>

            <div className="text-center p-8 glass-card dark-transition dark-hover" data-testid="feature-delivery">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{
                background: 'rgba(0, 240, 255, 0.15)',
                boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
              }}>
                <Award size={32} style={{ color: '#00F0FF' }} />
              </div>
              <h3 className="heading-2 mb-4" style={{ color: '#F8FAFC' }}>
                {getContent('feature3_title', t, 'home.features.telemetry.title', language)}
              </h3>
              <p className="body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                {getContent('feature3_description', t, 'home.features.telemetry.description', language)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-24 px-[7.6923%]" style={{
        background: 'rgba(30, 41, 59, 0.4)',
        backdropFilter: 'blur(8px)'
      }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="flex justify-between items-center mb-12">
            <h2 className="display-medium" style={{ color: '#F8FAFC' }}>
              {getContent('updates_title', t, 'home.buildLogs.title', language)}
            </h2>
            <Link 
              to="/build-logs" 
              className="btn-secondary inline-flex"
              data-testid="view-all-logs-btn"
            >
              {t('home.buildLogs.viewAll')}
              <ArrowRight size={20} />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#00F0FF' }} />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {latestLogs.map((log) => (
                <article 
                  key={log.id}
                  className="glass-card overflow-hidden dark-transition dark-hover"
                  data-testid={`home-log-card-${log.id}`}
                >
                  <div 
                    className="h-48 bg-cover bg-center"
                    style={{ backgroundImage: `url(${log.image})` }}
                  ></div>
                  <div className="p-6">
                    <p className="body-muted mb-2" style={{ color: '#94A3B8' }}>{log.date}</p>
                    <h3 className="heading-3 mb-3" style={{ color: '#F8FAFC' }}>
                      {getLocalizedField(log, 'title', language)}
                    </h3>
                    <p className="body-small mb-4 line-clamp-3" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                      {getLocalizedField(log, 'excerpt', language)}
                    </p>
                    <Link 
                      to={`/build-logs/${log.id}`}
                      className="text-base font-medium hover:text-[#06B6D4] transition-colors duration-300 inline-flex items-center gap-2"
                      style={{ color: '#00F0FF' }}
                    >
                      {t('home.buildLogs.readMore')}
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Achievements Section */}
      {featuredAchievements.length > 0 && (
        <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }} data-testid="home-achievements-section">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
              <div>
                <h2 className="display-medium mb-2" style={{ color: '#F8FAFC' }}>
                  {language === 'tr' ? 'Öne Çıkan Başarılarımız' : 'Our Featured Achievements'}
                </h2>
                <p className="body-medium" style={{ color: '#94A3B8' }}>
                  {language === 'tr' 
                    ? 'Ulusal ve uluslararası yarışmalarda elde ettiğimiz başarılar' 
                    : 'Our achievements in national and international competitions'}
                </p>
              </div>
              <Link 
                to="/achievements" 
                className="btn-secondary inline-flex whitespace-nowrap"
                data-testid="view-all-achievements-btn"
              >
                {language === 'tr' ? 'Tüm Başarılar' : 'View All Achievements'}
                <ArrowRight size={20} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredAchievements.map((achievement) => {
                const style = getFeaturedStyle(achievement.featured_color);
                return (
                  <div
                    key={achievement.id}
                    className="relative p-6 rounded-xl transition-all duration-300 cursor-pointer"
                    style={{
                      background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                      border: `1px solid ${style.borderColor}`,
                      boxShadow: `0 0 20px ${style.shadowColor}`
                    }}
                    onClick={() => setSelectedAchievement(achievement)}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = `0 0 40px ${style.shadowColor}`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 0 20px ${style.shadowColor}`;
                    }}
                    data-testid={`home-achievement-${achievement.id}`}
                  >
                    {/* Click indicator */}
                    <div className="absolute top-3 right-3 text-xs px-2 py-1 rounded-full" style={{
                      background: `${style.iconColor}20`,
                      color: style.iconColor,
                      border: `1px solid ${style.borderColor}`
                    }}>
                      {language === 'tr' ? 'Detay' : 'Details'}
                    </div>

                    {/* Trophy Icon */}
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                      style={{
                        background: `${style.iconColor}15`,
                        boxShadow: `0 0 20px ${style.shadowColor}`
                      }}
                    >
                      <Trophy size={28} style={{ color: style.iconColor }} />
                    </div>

                    {/* Year Badge */}
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar size={14} style={{ color: '#94A3B8' }} />
                      <span className="text-sm font-medium" style={{ color: '#94A3B8' }}>
                        {achievement.year}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold mb-2" style={{ color: style.iconColor }}>
                      {getLocalizedField(achievement, 'title', language)}
                    </h3>

                    {/* Ranking */}
                    <p className="text-sm font-semibold mb-3" style={{ color: '#F8FAFC' }}>
                      {getLocalizedField(achievement, 'ranking', language)}
                    </p>

                    {/* Location */}
                    {achievement.location && (
                      <div className="flex items-center gap-2 mb-4">
                        <MapPin size={14} style={{ color: '#94A3B8' }} />
                        <span className="text-sm" style={{ color: '#94A3B8' }}>
                          {getLocalizedField(achievement, 'location', language)}
                        </span>
                      </div>
                    )}

                    {/* Description */}
                    {achievement.description && (
                      <p className="text-sm leading-relaxed line-clamp-2" style={{ color: '#F8FAFC', opacity: 0.8 }}>
                        {getLocalizedField(achievement, 'description', language)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Quick Stats */}
            <div className="mt-12 flex flex-wrap justify-center gap-8">
              <div className="text-center">
                <span className="text-3xl font-bold" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.5)' }}>
                  {featuredAchievements.length}+
                </span>
                <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                  {language === 'tr' ? 'Önemli Başarı' : 'Major Achievements'}
                </p>
              </div>
              <div className="text-center">
                <span className="text-3xl font-bold" style={{ color: '#FFD700', textShadow: '0 0 10px rgba(255, 215, 0, 0.5)' }}>
                  2
                </span>
                <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                  {language === 'tr' ? 'Şampiyonluk' : 'Championships'}
                </p>
              </div>
              <div className="text-center">
                <span className="text-3xl font-bold" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.5)' }}>
                  5+
                </span>
                <p className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                  {language === 'tr' ? 'Yıllık Deneyim' : 'Years of Experience'}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Competition Documents Section */}
      <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }} data-testid="home-documents-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
            <div>
              <h2 className="display-medium mb-2 flex items-center gap-3" style={{ color: '#F8FAFC' }}>
                <FileText size={32} style={{ color: '#00F0FF' }} />
                {language === 'tr' ? 'Yarışma Dökümanları' : 'Competition Documents'}
              </h2>
              <p className="body-medium" style={{ color: '#94A3B8' }}>
                {language === 'tr' 
                  ? 'Teknik dokümantasyonumuza ve yarışma materyallerimize erişin' 
                  : 'Access our technical documentation and competition materials'}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Technical Design Report */}
            {(() => {
              const tdr = documents.find(d => d.category === 'technical_report');
              if (tdr) {
                const tdrName = language === 'tr' && tdr.name_tr ? tdr.name_tr : tdr.name;
                const tdrDesc = language === 'tr' && tdr.description_tr ? tdr.description_tr : tdr.description;
                const tdrUrl = tdr.youtube_url || `${process.env.REACT_APP_BACKEND_URL}/api/documents/${tdr.id}/download`;
                
                return (
                  <a
                    href={tdrUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass-card p-8 transition-all duration-300 hover:scale-[1.02] cursor-pointer group"
                    style={{ border: '1px solid rgba(0, 240, 255, 0.3)' }}
                    data-testid="home-doc-tdr"
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(0, 240, 255, 0.1)' }}
                      >
                        <Download size={28} style={{ color: '#00F0FF' }} />
                      </div>
                      <div>
                        <h3 className="heading-3 mb-1 group-hover:text-[#00F0FF] transition-colors" style={{ color: '#F8FAFC' }}>
                          {tdrName}
                        </h3>
                        <p className="text-sm" style={{ color: '#94A3B8' }}>
                          {language === 'tr' ? 'Teknik Tasarım Raporu' : 'Technical Design Report'}
                        </p>
                      </div>
                    </div>
                    <p className="body-small" style={{ color: '#F8FAFC', opacity: 0.8 }}>
                      {tdrDesc || (language === 'tr' 
                        ? 'İHA tasarımımızın detaylı teknik dokümantasyonu' 
                        : 'Detailed technical documentation of our UAV design')}
                    </p>
                  </a>
                );
              }
              return (
                <div 
                  className="glass-card p-8 opacity-60"
                  style={{ border: '1px solid rgba(148, 163, 184, 0.2)' }}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(148, 163, 184, 0.1)' }}
                    >
                      <Download size={28} style={{ color: '#94A3B8' }} />
                    </div>
                    <div>
                      <h3 className="heading-3 mb-1" style={{ color: '#F8FAFC' }}>
                        {language === 'tr' ? 'Teknik Tasarım Raporu' : 'Technical Design Report'}
                      </h3>
                      <p className="text-sm" style={{ color: '#94A3B8' }}>
                        {language === 'tr' ? 'Yakında' : 'Coming Soon'}
                      </p>
                    </div>
                  </div>
                  <p className="body-small" style={{ color: '#F8FAFC', opacity: 0.6 }}>
                    {language === 'tr' 
                      ? 'Dokümantasyon hazırlanıyor...' 
                      : 'Documentation in preparation...'}
                  </p>
                </div>
              );
            })()}

            {/* Proof of Flight */}
            {(() => {
              const pof = documents.find(d => d.category === 'proof_of_flight');
              if (pof) {
                const pofName = language === 'tr' && pof.name_tr ? pof.name_tr : pof.name;
                const pofDesc = language === 'tr' && pof.description_tr ? pof.description_tr : pof.description;
                const pofUrl = pof.youtube_url || `${process.env.REACT_APP_BACKEND_URL}/api/documents/${pof.id}/download`;
                const youtubeId = pof.youtube_url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]{11})/)?.[1];
                
                return (
                  <a
                    href={pofUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="glass-card p-8 transition-all duration-300 hover:scale-[1.02] cursor-pointer group overflow-hidden"
                    style={{ border: '1px solid rgba(0, 240, 255, 0.3)' }}
                    data-testid="home-doc-pof"
                  >
                    {youtubeId && (
                      <div className="relative rounded-lg overflow-hidden mb-4" style={{ aspectRatio: '16/9' }}>
                        <img 
                          src={`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`}
                          alt="Video thumbnail"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-colors">
                          <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                            <Play size={28} className="text-white ml-1" />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start gap-4 mb-4">
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: 'rgba(239, 68, 68, 0.1)' }}
                      >
                        <Video size={28} style={{ color: '#EF4444' }} />
                      </div>
                      <div>
                        <h3 className="heading-3 mb-1 group-hover:text-[#00F0FF] transition-colors" style={{ color: '#F8FAFC' }}>
                          {pofName}
                        </h3>
                        <p className="text-sm" style={{ color: '#94A3B8' }}>
                          {language === 'tr' ? 'Uçuş Kanıtı' : 'Proof of Flight'}
                        </p>
                      </div>
                    </div>
                    <p className="body-small" style={{ color: '#F8FAFC', opacity: 0.8 }}>
                      {pofDesc || (language === 'tr' 
                        ? 'Uçuş testlerimizin video kaydı' 
                        : 'Video recording of our flight tests')}
                    </p>
                  </a>
                );
              }
              return (
                <div 
                  className="glass-card p-8 opacity-60"
                  style={{ border: '1px solid rgba(148, 163, 184, 0.2)' }}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(148, 163, 184, 0.1)' }}
                    >
                      <Video size={28} style={{ color: '#94A3B8' }} />
                    </div>
                    <div>
                      <h3 className="heading-3 mb-1" style={{ color: '#F8FAFC' }}>
                        {language === 'tr' ? 'Uçuş Kanıtı' : 'Proof of Flight'}
                      </h3>
                      <p className="text-sm" style={{ color: '#94A3B8' }}>
                        {language === 'tr' ? 'Yakında' : 'Coming Soon'}
                      </p>
                    </div>
                  </div>
                  <p className="body-small" style={{ color: '#F8FAFC', opacity: 0.6 }}>
                    {language === 'tr' 
                      ? 'Video kayıtları hazırlanıyor...' 
                      : 'Video recordings in preparation...'}
                  </p>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      {/* Featured YouTube Videos Section */}
      {youtubeVideos.length > 0 && (
        <section className="py-24 px-[7.6923%]" style={{
          background: 'rgba(30, 41, 59, 0.4)',
          backdropFilter: 'blur(8px)'
        }} data-testid="home-youtube-section">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
              <div>
                <h2 className="display-medium mb-2 flex items-center gap-3" style={{ color: '#F8FAFC' }}>
                  <Youtube size={32} style={{ color: '#FF0000' }} />
                  {language === 'tr' ? 'Kanalımızdan Öne Çıkanlar' : 'Featured from Our Channel'}
                </h2>
                <p className="body-medium" style={{ color: '#94A3B8' }}>
                  {language === 'tr' 
                    ? 'Takımımızın en son videolarını izleyin' 
                    : 'Watch our team\'s latest videos'}
                </p>
              </div>
              <a 
                href="https://www.youtube.com/@kartalairiha" 
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex whitespace-nowrap"
                data-testid="view-youtube-channel-btn"
              >
                {language === 'tr' ? 'Kanala Git' : 'Visit Channel'}
                <ArrowRight size={20} />
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {youtubeVideos.map((video) => (
                <div
                  key={video.id}
                  className="relative rounded-xl overflow-hidden cursor-pointer group"
                  style={{
                    background: 'rgba(30, 41, 59, 0.8)',
                    border: '1px solid rgba(255, 0, 0, 0.2)'
                  }}
                  onClick={() => setSelectedVideo(video)}
                  data-testid={`home-video-${video.id}`}
                >
                  {/* Thumbnail */}
                  <div className="relative" style={{ aspectRatio: '16/9' }}>
                    <img
                      src={video.thumbnail || `https://img.youtube.com/vi/${video.video_id}/maxresdefault.jpg`}
                      alt={getLocalizedField(video, 'title', language) || 'Video thumbnail'}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${video.video_id}/hqdefault.jpg`;
                      }}
                    />
                    {/* Play Button Overlay */}
                    <div 
                      className="absolute inset-0 flex items-center justify-center transition-all duration-300"
                      style={{ background: 'rgba(0,0,0,0.3)' }}
                    >
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                        style={{ 
                          background: 'rgba(255, 0, 0, 0.9)',
                          boxShadow: '0 0 30px rgba(255, 0, 0, 0.5)'
                        }}
                      >
                        <Play size={28} fill="white" style={{ color: 'white', marginLeft: '4px' }} />
                      </div>
                    </div>
                    {/* Duration/Watch Badge */}
                    <div 
                      className="absolute bottom-2 right-2 px-2 py-1 rounded text-xs font-medium"
                      style={{ background: 'rgba(0,0,0,0.8)', color: '#F8FAFC' }}
                    >
                      {language === 'tr' ? 'İzle' : 'Watch'}
                    </div>
                  </div>
                  
                  {/* Video Title */}
                  <div className="p-4">
                    <h3 className="font-semibold line-clamp-2 transition-colors duration-300 group-hover:text-red-400" style={{ color: '#F8FAFC' }}>
                      {getLocalizedField(video, 'title', language) || 'Kartal Air Video'}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* YouTube Video Lightbox */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
          onClick={() => setSelectedVideo(null)}
          data-testid="video-lightbox-overlay"
        >
          <div 
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="video-lightbox-content"
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.8)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
              data-testid="video-lightbox-close"
            >
              <X size={20} style={{ color: '#F8FAFC' }} />
            </button>

            {/* YouTube Embed */}
            <div className="rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <iframe
                src={`https://www.youtube.com/embed/${selectedVideo.video_id}?autoplay=1&rel=0`}
                title={getLocalizedField(selectedVideo, 'title', language) || 'YouTube video'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>

            {/* Video Title Below */}
            <div className="mt-4 text-center">
              <h3 className="text-xl font-semibold" style={{ color: '#F8FAFC' }}>
                {getLocalizedField(selectedVideo, 'title', language) || 'Kartal Air Video'}
              </h3>
            </div>
          </div>
        </div>
      )}

      {/* Achievement Detail Popup */}
      {selectedAchievement && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' }}
          onClick={() => setSelectedAchievement(null)}
          data-testid="achievement-popup-overlay"
        >
          <div 
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl"
            style={{
              background: getFeaturedStyle(selectedAchievement.featured_color).bgGradient,
              border: `2px solid ${getFeaturedStyle(selectedAchievement.featured_color).borderColor}`,
              boxShadow: `0 0 60px ${getFeaturedStyle(selectedAchievement.featured_color).shadowColor}`
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="achievement-popup-content"
          >
            {/* Close Button */}
            <button
              onClick={() => setSelectedAchievement(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300"
              style={{
                background: 'rgba(15, 23, 42, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.8)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(15, 23, 42, 0.8)';
              }}
              data-testid="achievement-popup-close"
            >
              <X size={20} style={{ color: '#F8FAFC' }} />
            </button>

            {/* Achievement Image */}
            {selectedAchievement.image && (
              <div className="relative h-64 overflow-hidden rounded-t-2xl">
                <img
                  src={getImageUrl(selectedAchievement.image)}
                  alt={getLocalizedField(selectedAchievement, 'title', language)}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{
                  background: 'linear-gradient(to top, rgba(15, 23, 42, 1) 0%, transparent 60%)'
                }} />
              </div>
            )}

            {/* Content */}
            <div className={`p-8 ${selectedAchievement.image ? '-mt-16 relative z-10' : ''}`}>
              {/* Trophy Icon & Year */}
              <div className="flex items-center gap-4 mb-6">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center"
                  style={{
                    background: `${getFeaturedStyle(selectedAchievement.featured_color).iconColor}20`,
                    boxShadow: `0 0 30px ${getFeaturedStyle(selectedAchievement.featured_color).shadowColor}`
                  }}
                >
                  <Trophy size={32} style={{ color: getFeaturedStyle(selectedAchievement.featured_color).iconColor }} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar size={16} style={{ color: '#94A3B8' }} />
                    <span className="text-lg font-medium" style={{ color: '#94A3B8' }}>
                      {selectedAchievement.year}
                    </span>
                  </div>
                  {selectedAchievement.location && (
                    <div className="flex items-center gap-2">
                      <MapPin size={16} style={{ color: '#94A3B8' }} />
                      <span className="text-sm" style={{ color: '#94A3B8' }}>
                        {getLocalizedField(selectedAchievement, 'location', language)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Title */}
              <h2 className="text-3xl font-bold mb-3" style={{ 
                color: getFeaturedStyle(selectedAchievement.featured_color).iconColor,
                textShadow: `0 0 20px ${getFeaturedStyle(selectedAchievement.featured_color).shadowColor}`
              }}>
                {getLocalizedField(selectedAchievement, 'title', language)}
              </h2>

              {/* Ranking Badge */}
              <div 
                className="inline-block px-4 py-2 rounded-full mb-6"
                style={{
                  background: `${getFeaturedStyle(selectedAchievement.featured_color).iconColor}20`,
                  border: `1px solid ${getFeaturedStyle(selectedAchievement.featured_color).borderColor}`
                }}
              >
                <span className="font-semibold" style={{ color: getFeaturedStyle(selectedAchievement.featured_color).iconColor }}>
                  {getLocalizedField(selectedAchievement, 'ranking', language)}
                </span>
              </div>

              {/* Description */}
              {selectedAchievement.description && (
                <div className="mb-6">
                  <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>
                    {language === 'tr' ? 'Açıklama' : 'Description'}
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                    {getLocalizedField(selectedAchievement, 'description', language)}
                  </p>
                </div>
              )}

              {/* Action Button */}
              <Link
                to="/achievements"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300"
                style={{
                  background: getFeaturedStyle(selectedAchievement.featured_color).iconColor,
                  color: '#0F172A'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 10px 30px ${getFeaturedStyle(selectedAchievement.featured_color).shadowColor}`;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={() => setSelectedAchievement(null)}
                data-testid="achievement-popup-view-all"
              >
                {language === 'tr' ? 'Tüm Başarıları Gör' : 'View All Achievements'}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
